/* Deflate and inflate with the browser's own CompressionStream / DecompressionStream: share codes use raw
   deflate, PDF streams and WOFF font tables use zlib ('deflate'). */

type Format = 'deflate' | 'deflate-raw';

/** The browsers' (de)compression streams; TypeScript 4.9's DOM types do not describe them yet. */
type ByteStream = { readable: ReadableStream<Uint8Array>; writable: WritableStream<Uint8Array> };

async function pipe(bytes: Uint8Array, stream: ByteStream, maxBytes: number, tooLarge: () => Error): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  writer.write(bytes).catch(() => undefined);
  writer.close().catch(() => undefined);
  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  for (let part = await reader.read(); !part.done; part = await reader.read()) {
    chunks.push(part.value);
    size += part.value.length;
    if (size > maxBytes) throw tooLarge();
  }
  const out = new Uint8Array(size);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}

const unlimited = () => new Error('unreachable');

export const compress = (bytes: Uint8Array, format: Format) =>
  pipe(bytes, new CompressionStream(format) as unknown as ByteStream, Infinity, unlimited);

/** Inflates, stopping with `tooLarge()` past `maxBytes` (a hostile input can inflate a thousandfold). */
export const decompress = (bytes: Uint8Array, format: Format, maxBytes = Infinity, tooLarge = unlimited) =>
  pipe(bytes, new DecompressionStream(format) as unknown as ByteStream, maxBytes, tooLarge);
